/**
 * Pull-to-refresh, as a state machine that knows nothing about the DOM.
 *
 * The gesture is four decisions and every one of them is a way to get it wrong:
 * whether the box was actually at the top when the finger landed, whether the
 * drag went down rather than up, whether it went far enough, and whether enough
 * time has passed since the last refresh to do another. Written as a reducer so
 * all four can be tested by calling a function — this app has no DOM test
 * environment, and a gesture asserted from its source is not a gesture that was
 * tested.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It never *prevents* a scroll. A pull that
 * does not qualify simply leaves the state idle and the browser does whatever it
 * would have done, which on a bounded, `overscroll-behavior: contain` scroller
 * is the ordinary rubber-band. Dragging up at the top — the case that used to
 * reveal the black behind the app — is not a refresh and is not intercepted; it
 * is contained by CSS, one layer down, where containment belongs.
 */

/** How far down the finger must travel before the pull counts. */
export const PULL_THRESHOLD_PX = 64;

/**
 * How long after a refresh another one is ignored.
 *
 * A pull is easy to repeat by accident — the list springs back under the thumb
 * and the same drag reads as a second gesture. Without this a customer holding
 * the top of a list issues a request per bounce.
 */
export const REFRESH_COOLDOWN_MS = 1500;

/**
 * The furthest the indicator is dragged, however hard the pull.
 *
 * Past the threshold the gesture is already decided, so the extra travel is
 * feedback rather than information; capping it keeps a long drag from pushing
 * the indicator into the content.
 */
export const PULL_MAX_PX = 96;

export type PullPhase =
  /** No gesture, or one that did not qualify. */
  | 'idle'
  /** Dragging down from the top, not yet far enough. */
  | 'pulling'
  /** Far enough: releasing now refreshes. */
  | 'armed'
  /** The caller's refresh is running. */
  | 'refreshing';

export interface PullState {
  readonly phase: PullPhase;
  /** How far the indicator should be shown, in pixels. Zero unless dragging. */
  readonly distance: number;
  /** When the last refresh started, for the cooldown. */
  readonly lastRefreshAt: number | null;
}

export const initialPullState: PullState = {
  phase: 'idle',
  distance: 0,
  lastRefreshAt: null,
};

export type PullEvent =
  | { readonly type: 'start'; readonly y: number; readonly scrollTop: number; readonly now: number }
  | { readonly type: 'move'; readonly y: number; readonly scrollTop: number }
  | { readonly type: 'end'; readonly now: number }
  | { readonly type: 'cancel' }
  | { readonly type: 'refreshed' };

interface Gesture {
  readonly startY: number;
}

let gesture: Gesture | null = null;

/** Exposed for tests: a reducer with a module-level cursor is not reusable. */
export const resetPullGesture = (): void => {
  gesture = null;
};

/**
 * The next state, and whether the caller should run its refresh.
 *
 * `refresh` is returned rather than performed: the decision is pure, the effect
 * is the hook's.
 */
export function reducePull(
  state: PullState,
  event: PullEvent,
): { readonly state: PullState; readonly refresh: boolean } {
  switch (event.type) {
    case 'start': {
      /*
       * Only from a genuine top. `scrollTop > 0` means the customer is reading
       * the middle of a list and a downward drag is a scroll, not a request.
       *
       * `<= 0` and not `=== 0` because a rubber-banding scroller reports a small
       * negative while it is bouncing back, and a finger landing during that
       * bounce is still a finger at the top.
       */
      if (state.phase === 'refreshing') return { state, refresh: false };
      if (event.scrollTop > 0) {
        gesture = null;
        return { state: { ...state, phase: 'idle', distance: 0 }, refresh: false };
      }
      gesture = { startY: event.y };
      return { state: { ...state, phase: 'pulling', distance: 0 }, refresh: false };
    }

    case 'move': {
      if (!gesture || state.phase === 'refreshing') return { state, refresh: false };

      /*
       * Left the top mid-gesture — the customer started at the top and scrolled
       * down into the list. That is a scroll that happened to begin where a pull
       * begins, and treating it as a pull would refresh the screen out from
       * under someone who was reading.
       */
      if (event.scrollTop > 0) {
        gesture = null;
        return { state: { ...state, phase: 'idle', distance: 0 }, refresh: false };
      }

      const travelled = event.y - gesture.startY;

      /*
       * Upward, or back above where it started. This is the case the whole
       * change is about: dragging up at the top must not arm anything, and must
       * not be treated as a gesture at all. It stays `pulling` rather than
       * cancelling, so a customer who overshoots upward and comes back down
       * still gets their refresh.
       */
      if (travelled <= 0) {
        return { state: { ...state, phase: 'pulling', distance: 0 }, refresh: false };
      }

      const distance = Math.min(travelled, PULL_MAX_PX);
      return {
        state: {
          ...state,
          phase: travelled >= PULL_THRESHOLD_PX ? 'armed' : 'pulling',
          distance,
        },
        refresh: false,
      };
    }

    case 'end': {
      /*
       * A release while a refresh is running belongs to no gesture of ours —
       * `start` refused to begin one. Falling through would set `idle` and take
       * the indicator away with the request still in flight, which reads as the
       * refresh having finished instantly.
       */
      if (state.phase === 'refreshing') {
        gesture = null;
        return { state, refresh: false };
      }

      const armed = state.phase === 'armed';
      gesture = null;

      if (!armed) return { state: { ...state, phase: 'idle', distance: 0 }, refresh: false };

      /*
       * The cooldown, checked on release rather than on start: a pull that began
       * during the window is not wrong, it is just early, and judging it at the
       * end gives it the longest chance of being allowed.
       */
      const cooling =
        state.lastRefreshAt !== null && event.now - state.lastRefreshAt < REFRESH_COOLDOWN_MS;

      if (cooling) return { state: { ...state, phase: 'idle', distance: 0 }, refresh: false };

      return {
        state: { phase: 'refreshing', distance: PULL_THRESHOLD_PX, lastRefreshAt: event.now },
        refresh: true,
      };
    }

    case 'cancel': {
      gesture = null;
      if (state.phase === 'refreshing') return { state, refresh: false };
      return { state: { ...state, phase: 'idle', distance: 0 }, refresh: false };
    }

    case 'refreshed':
      return { state: { ...state, phase: 'idle', distance: 0 }, refresh: false };

    default:
      return { state, refresh: false };
  }
}

/**
 * What a screen reader is told, and it is never a running commentary.
 *
 * Announced only for the two states that are events — the refresh starting and
 * finishing. `pulling` and `armed` change many times a second while a finger
 * moves, and an `aria-live` region that narrated them would talk over the
 * customer continuously. Empty string means "say nothing", which is what an
 * unchanged live region does.
 */
export const pullStatusText = (phase: PullPhase, refreshed: boolean): string => {
  if (phase === 'refreshing') return 'Обновляем…';
  return refreshed ? 'Обновлено' : '';
};
